/**
 * Letter rules for the "İnisiallar" game: two letters are picked from the
 * Azerbaijani alphabet and players name real people whose name and surname
 * carry those letters at the agreed positions (first, second or last letter).
 *
 * Pure functions only — the game flow lives in initialsGame.ts.
 */

export const AZ_ALPHABET = [
  'A', 'B', 'C', 'Ç', 'D', 'E', 'Ə', 'F', 'G', 'Ğ', 'H', 'X', 'I', 'İ', 'J', 'K',
  'Q', 'L', 'M', 'N', 'O', 'Ö', 'P', 'R', 'S', 'Ş', 'T', 'U', 'Ü', 'V', 'Y', 'Z',
] as const;

const AZ_SET = new Set<string>(AZ_ALPHABET);

export type LetterPos = 'first' | 'second' | 'last';

export const LETTER_POS_LABELS: Record<LetterPos, string> = {
  first: 'birinci',
  second: 'ikinci',
  last: 'sonuncu',
};

export interface LetterRule {
  name: LetterPos;
  surname: LetterPos;
}

export interface RulePreset {
  id: string;
  title: string;
  rule: LetterRule;
}

export const RULE_PRESETS: RulePreset[] = [
  { id: 'first-first', title: 'Baş hərflər', rule: { name: 'first', surname: 'first' } },
  { id: 'last-first', title: 'Adın sonu + soyadın başı', rule: { name: 'last', surname: 'first' } },
  { id: 'second-second', title: 'İkinci hərflər', rule: { name: 'second', surname: 'second' } },
  { id: 'last-last', title: 'Son hərflər', rule: { name: 'last', surname: 'last' } },
  { id: 'first-last', title: 'Adın başı + soyadın sonu', rule: { name: 'first', surname: 'last' } },
];

export const ruleKey = (rule: LetterRule) => `${rule.name}-${rule.surname}`;

export const describeRule = (rule: LetterRule) =>
  RULE_PRESETS.find(p => ruleKey(p.rule) === ruleKey(rule))?.title ??
  `Adın ${LETTER_POS_LABELS[rule.name]} + soyadın ${LETTER_POS_LABELS[rule.surname]} hərfi`;

/**
 * How a name is read:
 * - `spelling` — as it is written in its own language ("Gérard" → G). Latin
 *   diacritics are dropped (É → E) and I/İ count as the same letter, since
 *   foreign spellings make no such distinction.
 * - `sound` — as it is pronounced. The player writes the name the way it is
 *   transcribed in Azerbaijani ("Jerar Depardye" → J, D), which is phonetic,
 *   so only letters of the Azerbaijani alphabet are allowed.
 */
export type ReadingMode = 'spelling' | 'sound';

export const READING_MODE_LABELS: Record<ReadingMode, { title: string; description: string }> = {
  spelling: {
    title: 'Yazılışa görə',
    description: 'Ad öz dilində necə yazılırsa (Gérard Depardieu → G, D).',
  },
  sound: {
    title: 'Tələffüzə görə',
    description: 'Ad necə tələffüz olunursa, Azərbaycan dilində elə yazılır (Jerar Depardye → J, D).',
  },
};

/** Azerbaijani-aware upper case: i → İ, ı → I (plain toUpperCase maps i → I). */
export const azUpper = (s: string) => s.replace(/i/g, 'İ').replace(/ı/g, 'I').toUpperCase();

/** Azerbaijani-aware lower case: İ → i, I → ı (plain toLowerCase adds a combining dot to İ). */
export const azLower = (s: string) => s.replace(/İ/g, 'i').replace(/I/g, 'ı').toLowerCase();

/** Upper-cases one character and maps Latin letters with diacritics onto their Azerbaijani base. */
const foldChar = (ch: string): string => {
  const upper = azUpper(ch);
  if (AZ_SET.has(upper)) return upper;
  const base = upper.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return AZ_SET.has(base) ? base : upper;
};

// Cased scripts only (Latin, Cyrillic, Greek) — enough for names typed in this game.
const isLetter = (ch: string) => ch.toLowerCase() !== ch.toUpperCase();

/** The letters of one word, folded and upper-cased; hyphens, apostrophes and dots are skipped. */
export const wordLetters = (word: string): string[] => Array.from(word.normalize('NFC')).filter(isLetter).map(foldChar);

/** Splits a full name into its words, dropping stray punctuation-only pieces. */
export const splitName = (fullName: string): string[] =>
  fullName
    .trim()
    .split(/\s+/)
    .filter(word => wordLetters(word).length > 0);

export const letterAt = (word: string, pos: LetterPos): string | null => {
  const letters = wordLetters(word);
  if (pos === 'first') return letters[0] ?? null;
  if (pos === 'second') return letters[1] ?? null;
  return letters.length > 0 ? letters[letters.length - 1] : null;
};

/** Index of the letter a position points to, for highlighting it in the UI. */
export const letterIndexAt = (word: string, pos: LetterPos): number => {
  const count = wordLetters(word).length;
  if (count === 0) return -1;
  if (pos === 'first') return 0;
  if (pos === 'second') return count > 1 ? 1 : -1;
  return count - 1;
};

export const lettersMatch = (actual: string | null, expected: string, mode: ReadingMode) => {
  if (!actual) return false;
  if (actual === expected) return true;
  return mode === 'spelling' && (actual === 'I' || actual === 'İ') && (expected === 'I' || expected === 'İ');
};

/** Letters that are not in the Azerbaijani alphabet (only an error in `sound` mode). */
export const foreignLetters = (fullName: string): string[] => {
  const found = new Set<string>();
  Array.from(fullName.normalize('NFC')).forEach(ch => {
    if (isLetter(ch) && !AZ_SET.has(azUpper(ch))) found.add(azUpper(ch));
  });
  return Array.from(found);
};

/**
 * Default split of the words: the first is the name and the last the surname.
 * Players can re-assign them ("Leonardo da Vinci", "Jean-Claude Van Damme").
 */
export const defaultParts = (words: string[]) => ({ nameIdx: 0, surnameIdx: Math.max(words.length - 1, 0) });

/** Key used to stop the same person from being named twice. */
export const personKey = (fullName: string) =>
  splitName(fullName)
    .map(word => azLower(wordLetters(word).join('')))
    .join(' ');

export interface AnswerCheckInput {
  fullName: string;
  nameIdx: number;
  surnameIdx: number;
  description: string;
  rule: LetterRule;
  letters: { name: string; surname: string };
  mode: ReadingMode;
  /** Keys (personKey) of people already named in this match. */
  usedKeys?: string[];
}

export interface AnswerCheck {
  ok: boolean;
  words: string[];
  nameLetter: string | null;
  surnameLetter: string | null;
  nameOk: boolean;
  surnameOk: boolean;
  problems: string[];
}

export const MIN_DESCRIPTION_LENGTH = 3;

/** Everything the system can verify on its own; whether the person is real is up to the opponent. */
export const checkAnswer = (input: AnswerCheckInput): AnswerCheck => {
  const words = splitName(input.fullName);
  const problems: string[] = [];
  const nameWord = words[input.nameIdx];
  const surnameWord = words[input.surnameIdx];

  if (words.length < 2) problems.push('Həm ad, həm soyad yazın.');
  else if (input.nameIdx === input.surnameIdx || !nameWord || !surnameWord) problems.push('Ad və soyad fərqli sözlər olmalıdır.');
  else if (input.nameIdx > input.surnameIdx) problems.push('Ad soyaddan əvvəl gəlməlidir.');

  const nameLetter = words.length >= 2 && nameWord ? letterAt(nameWord, input.rule.name) : null;
  const surnameLetter = words.length >= 2 && surnameWord ? letterAt(surnameWord, input.rule.surname) : null;
  const nameOk = lettersMatch(nameLetter, input.letters.name, input.mode);
  const surnameOk = lettersMatch(surnameLetter, input.letters.surname, input.mode);

  if (problems.length === 0) {
    if (!nameOk) {
      problems.push(
        `Adın ${LETTER_POS_LABELS[input.rule.name]} hərfi «${input.letters.name}» olmalıdır${nameLetter ? `, «${nameLetter}» yazılıb` : ''}.`
      );
    }
    if (!surnameOk) {
      problems.push(
        `Soyadın ${LETTER_POS_LABELS[input.rule.surname]} hərfi «${input.letters.surname}» olmalıdır${surnameLetter ? `, «${surnameLetter}» yazılıb` : ''}.`
      );
    }
  }

  if (input.mode === 'sound') {
    const foreign = foreignLetters(input.fullName);
    if (foreign.length > 0) {
      problems.push(`Tələffüzə görə oyunda adı Azərbaycan hərfləri ilə yazın (${foreign.join(', ')} yoxdur).`);
    }
  }

  if (input.usedKeys?.includes(personKey(input.fullName))) problems.push('Bu şəxs artıq deyilib.');

  if (input.description.trim().length < MIN_DESCRIPTION_LENGTH) problems.push('Bu şəxsin kim olduğunu qısaca yazın.');

  return { ok: problems.length === 0, words, nameLetter, surnameLetter, nameOk, surnameOk, problems };
};

/**
 * Whether a letter can sit at a position at all — no Azerbaijani word starts
 * with Ğ, so it is never offered as a first letter.
 */
export const letterAllowedAt = (letter: string, pos: LetterPos) => !(letter === 'Ğ' && pos === 'first');

/**
 * How often the draw offers each letter, by position. Names start with
 * consonants like A, M, S, R far more often than with Ü or J, while second and
 * last letters are mostly vowels — weighting keeps drawn pairs playable.
 * Letters left out get weight 1; 0 means never drawn.
 */
const LETTER_WEIGHTS: Record<LetterPos, Partial<Record<string, number>>> = {
  first: {
    A: 6, M: 6, S: 6, R: 5, K: 5, T: 5, B: 5, D: 5, E: 4, F: 4, H: 4, L: 4, N: 4, P: 4, Q: 4, V: 4,
    C: 3, G: 3, İ: 3, J: 3, O: 3, Ə: 3, Z: 3, Ş: 3, X: 2, Y: 2, Ç: 2, U: 2, Ü: 1, Ö: 1, I: 0, Ğ: 0,
  },
  second: {
    A: 6, E: 6, İ: 5, O: 5, Ə: 4, U: 4, R: 4, L: 4, N: 3, I: 2, Ü: 2, H: 2, M: 2, S: 2, Y: 2,
    Ö: 1, Ğ: 0, J: 0, Q: 0, C: 0,
  },
  last: {
    A: 6, Ə: 5, E: 4, İ: 4, N: 5, R: 5, S: 4, L: 4, V: 4, Y: 3, O: 3, D: 3, M: 3, K: 3, T: 3, Z: 3, U: 2, H: 2, X: 2, I: 2,
    B: 1, Ç: 1, F: 1, G: 1, J: 0, P: 1, Q: 1, Ş: 1, C: 1, Ö: 0, Ü: 1, Ğ: 0,
  },
};

const letterWeight = (letter: string, pos: LetterPos) =>
  letterAllowedAt(letter, pos) ? LETTER_WEIGHTS[pos][letter] ?? 1 : 0;

export const randomLetter = (pos: LetterPos, random: () => number = Math.random): string => {
  const total = AZ_ALPHABET.reduce((sum, l) => sum + letterWeight(l, pos), 0);
  let roll = random() * total;
  for (const letter of AZ_ALPHABET) {
    roll -= letterWeight(letter, pos);
    if (roll < 0) return letter;
  }
  return AZ_ALPHABET.find(l => letterWeight(l, pos) > 0)!;
};

/**
 * Letters for a round chosen by the platform. Pairs already played in this
 * match (same rule and letters) are avoided when possible.
 */
export const drawLetters = (
  rule: LetterRule,
  played: { rule: LetterRule; letters: { name: string; surname: string } }[] = [],
  random: () => number = Math.random
) => {
  const seen = new Set(played.map(p => `${ruleKey(p.rule)}:${p.letters.name}${p.letters.surname}`));
  let letters = { name: randomLetter(rule.name, random), surname: randomLetter(rule.surname, random) };
  for (let i = 0; i < 20 && seen.has(`${ruleKey(rule)}:${letters.name}${letters.surname}`); i++) {
    letters = { name: randomLetter(rule.name, random), surname: randomLetter(rule.surname, random) };
  }
  return letters;
};
